import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { showBanner } from 'workflow/common/common.actions'

import { sdkTagsKeys } from 'common/api/queryKeys/sdkTagsKeys'
import Button from 'common/components/button'
import Modal from 'common/components/Modal'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import { createSdkTag, SdkTag } from './api'

type Props = {
  open: boolean
  onClose: () => void
  onCreated: (newTagId: string) => void
}

const CreateTagModal = ({ open, onClose, onCreated }: Props) => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    if (name.length > 64) {
      setError('Name must be 64 characters or fewer.')
      return
    }
    setIsCreating(true)
    setError('')
    try {
      const updated = await createSdkTag({ accountId, name })
      queryClient.setQueryData<SdkTag[]>(sdkTagsKeys.list(accountId), updated)
      dispatch(showBanner({ type: 'success', content: 'Tag created' }))
      const newTag = updated[0]
      onCreated(newTag?.tag_id ?? '')
      handleClose()
    } catch {
      setError('Failed to create tag. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const handleClose = () => {
    setName('')
    setError('')
    onClose()
  }

  return (
    <Modal
      visible={open}
      onClose={handleClose}
      title="New tag"
      footer={
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={isCreating}
          >
            {isCreating ? 'Creating…' : 'Create'}
          </Button>
        </div>
      }
    >
      <div className="mt-2">
        <label
          htmlFor="sdk-tag-name"
          className="mb-1 block text-sm font-medium text-coolGray-800"
        >
          Name
        </label>
        <input
          id="sdk-tag-name"
          data-testid="sdk-tag-name-input"
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            if (error) {
              setError('')
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleCreate()
            }
          }}
          maxLength={64}
          placeholder="e.g. UK site — production"
          className="w-full rounded border border-coolGray-300 px-3 py-2 text-sm focus:border-maroon-400 focus:outline-none"
        />
        {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
      </div>
    </Modal>
  )
}

export default CreateTagModal
